import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { photos as staticPhotos } from "../mock";

const PhotoGallery = forwardRef(({ extraPhotos = [] }, ref) => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  // Combine static photos with customer-uploaded ones
  const photos = [...staticPhotos, ...extraPhotos];
  const total = photos.length;

  const openAt = (i) => { setIdx(i); setOpen(true); };
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  // Allow parent to open a specific customer photo
  useImperativeHandle(ref, () => ({
    openBySrc: (src) => {
      const i = photos.findIndex((p) => p === src);
      if (i >= 0) openAt(i);
    },
  }));

  return (
    <>
      <div className="relative w-full">
        <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[420px] rounded-2xl overflow-hidden">
          <button onClick={() => openAt(0)} className="col-span-2 row-span-2 overflow-hidden group">
            <img src={photos[0]} alt="BAAN MAE" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </button>
          {photos.slice(1, 5).map((p, i) => (
            <button key={i} onClick={() => openAt(i + 1)} className="overflow-hidden group">
              <img src={p} alt={`Photo ${i + 2}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </button>
          ))}
        </div>
        <div className="md:hidden h-72 rounded-xl overflow-hidden relative">
          <div className="flex h-full overflow-x-auto snap-x snap-mandatory no-scrollbar">
            {photos.map((p, i) => (
              <button
                key={i}
                onClick={() => openAt(i)}
                className="snap-center flex-shrink-0 w-full h-full"
              >
                <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {photos.slice(0, Math.min(total, 6)).map((_, i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-white/70" />
            ))}
          </div>
        </div>
        <button
          onClick={() => openAt(0)}
          className="absolute bottom-4 right-4 flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow"
        >
          <Camera className="h-4 w-4" />
          Voir les {total}+ photos
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
          <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full">
            <X className="h-6 w-6" />
          </button>
          <button onClick={prev} className="absolute left-4 text-white p-3 hover:bg-white/10 rounded-full">
            <ChevronLeft className="h-8 w-8" />
          </button>
          <img src={photos[idx]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" />
          <button onClick={next} className="absolute right-4 text-white p-3 hover:bg-white/10 rounded-full">
            <ChevronRight className="h-8 w-8" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white text-sm">
            <span>{idx + 1} / {total}</span>
            {idx >= staticPhotos.length && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-[#1B7B71] text-white px-2 py-0.5 rounded">Photo client</span>
            )}
          </div>
        </div>
      )}
    </>
  );
});

PhotoGallery.displayName = "PhotoGallery";

export default PhotoGallery;
